import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

import { lazyComponent } from '../lib/lazyPage';
import {
  createShortcutHandler,
  isTouchOnlyDevice,
  registerNewTicketAction,
  runNewTicketAction,
} from '../lib/shortcuts';

// Prehľad skratiek otvorí málokto a málokedy — nepatrí do hlavného balíka.
const ShortcutsDialog = lazyComponent(() => import('../components/ShortcutsDialog'), 'ShortcutsDialog');

/**
 * Klávesové skratky, zapojené raz v ráme appky (`AppShell`).
 *
 * Vracia prvok s prehľadom skratiek, ktorý treba vykresliť — dialóg žije tu,
 * aby rám appky nemusel vedieť, čo v ňom je.
 */
export const useShortcuts = ({
  openSearch,
  toggleSidebar,
}: {
  openSearch: () => void;
  /** „[“ — bočný panel na ikony a späť (iba desktop, rám appky). */
  toggleSidebar?: () => void;
}): ReactNode => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  // Posledná verzia bez toho, aby sa poslucháč pri každom prekreslení rámu
  // odhlasoval a prihlasoval — a s ním aj rozpísané „G".
  const openSearchRef = useRef(openSearch);
  openSearchRef.current = openSearch;
  const toggleSidebarRef = useRef(toggleSidebar);
  toggleSidebarRef.current = toggleSidebar;
  const pathnameRef = useRef('');
  pathnameRef.current = useLocation().pathname;

  useEffect(() => {
    if (typeof window === 'undefined' || isTouchOnlyDevice(window.matchMedia?.bind(window))) {
      return undefined;
    }
    const handler = createShortcutHandler({
      navigate: (to) => navigate(to),
      openSearch: () => openSearchRef.current(),
      openHelp: () => setHelpOpen(true),
      toggleSidebar: () => toggleSidebarRef.current?.(),
      newTicket: runNewTicketAction,
      newTicketElsewhere: () => {
        // Ticket patrí vždy do projektu. Mimo dosky preto „C" zavedie tam, kde
        // sa projekt dá vybrať, namiesto toho, aby potichu nespravilo nič.
        const onProject = /^\/projects\/\d+/.exec(pathnameRef.current);
        if (onProject) {
          navigate(`${onProject[0]}?tab=tickety`);
          toast('Na doske projektu stlač C ešte raz.');
        } else {
          navigate('/projects');
          toast('Vyber projekt — na jeho doske založí C nový ticket.');
        }
      },
    });
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return helpOpen ? (
    <Suspense fallback={null}>
      <ShortcutsDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Suspense>
  ) : null;
};

/**
 * Prihlási „C" (nový ticket) za obrazovku, ktorá ho vie založiť. Jeden riadok
 * v doske projektu; pri odchode z dosky sa skratka sama odhlási.
 */
export const useNewTicketShortcut = (action: () => void): void => {
  const actionRef = useRef(action);
  actionRef.current = action;
  useEffect(() => registerNewTicketAction(() => actionRef.current()), []);
};
